/* eslint-disable vue/one-component-per-file */
import RefLink from '@/components/ref-link/RefLink';
import PreSeasonOverviewHeader from '@/preSeason/components/PreSeasonOverviewHeader';
import PreSeasonSummaryLayout from '@/preSeason/layouts/PreSeasonSummaryLayout.vue';
import BikeGarageApp from '@/bikeGarage/BikeGarageApp.vue';
import TrekMarquee from '@/components/TrekMarquee';
import ImageComponent from '@/components/ImageComponent';
import ApplePayIcon from '@/icons/ApplePayIcon.vue';
import IdealIcon from '@/icons/IdealIcon.vue';
import LiveChat from '@/components/LiveChat';
import B2cMiniCart from '@/components/mini-cart/b2c/B2cMiniCart.vue';
import B2bMiniCart from '@/components/mini-cart/b2b/B2bMiniCart.vue';
import {I18n, loadLanguageAsync} from '@/utils/i18n-setup-utils';
import {PdlBadge} from '@pedal/pdl-badge';
import {PdlCollapse, PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlLink} from '@pedal/pdl-link';
import {PdlButton} from '@pedal/pdl-button';
import {PdlMediaObject, PdlMediaObjectList} from '@pedal/pdl-media-object';
import {PdlTabPane, PdlTabs} from '@pedal/pdl-tabs';
import {PdlIcon} from '@pedal/pdl-icon';
import {PdlToast} from '@pedal/pdl-toast';
import {PdlSectionHeader, PdlHeading, PdlSubheading} from '@pedal/pdl-section-header';
import {PdlToggleSwitch} from '@pedal/pdl-toggle-switch';
import {PdlCallout} from '@pedal/pdl-callout';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlDialog} from '@pedal/pdl-dialog';
import {PdlCarousel, PdlCarouselSlide} from '@pedal/pdl-carousel';
import camelCase from 'lodash/camelCase';
import first from 'lodash/first';
import upperFirst from 'lodash/upperFirst';
import {createApp} from 'vue';
import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import store from './store';
import VueTippy from 'vue-tippy';

// Vue Nav Components
import NavCategoriesLink from '@/navigation/NavCategoriesLink';
import NavUtilityBarB2bContainer from './navigation/containers/NavUtilityBarB2bContainer.vue';
import NavUtilityBarControl from '@/navigation/components/NavUtilityBarControl';
import NavBar from '@/navigation/NavBar.vue';
import AccountSelector from '@/components/account-selector/AccountSelector';
import VendorSelector from '@/components/vendor-selector/VendorSelector';
import NotificationsNav from '@/components/containers/notifications/NotificationsNav';
import NotificationsDropdown from '@/components/containers/notifications/NotificationsDropdown';
import NavUtilityBarVendorSelector from '@/navigation/components/NavUtilityBarVendorSelector';

// Mixins
import trackGlobalEvents from './mixins/tracking/track-global-events';
import AuthHelpers from '@/utils/auth-helpers.js';
import {initInterceptors} from '@/utils/axios/toast-interceptor';

// Sets initial vue container and upon data-vue-dynamic-content, passes in element to be rendered
const vueConfig = async function (container = '#app') {
  if (!container?.attributes?.['data-vue-dynamic-content']) {
    if (!document.getElementById(container?.split('#')?.[1])) {
      container = '#page';
    }
  }

  const userEmail = store.state?.user?.userEmail;
  const customer = store.state?.user?.customer;
  const authHelpers = AuthHelpers({userEmail, customer}, store.dispatch);
  await authHelpers.handleLogin();

  let app = mountVueAppToElement(container);
  window.store = store;
  window.vm = app;
};
const mountVueAppToElement = function (container) {
  const app = createApp({
    components: {
      PreSeasonOverviewHeader,
      PreSeasonSummaryLayout,
      BikeGarageApp,
      TrekMarquee,
      ImageComponent,
      LiveChat,
      PdlBadge,
      PdlCollapse,
      PdlCollapseItem,
      PdlLink,
      PdlButton,
      PdlMediaObject,
      PdlMediaObjectList,
      PdlTabs,
      PdlTabPane,
      PdlToggleSwitch,
      PdlCallout,
      PdlLoading,
      PdlDialog,
      RefLink,
      PdlSectionHeader,
      PdlHeading,
      PdlSubheading,
      PdlIcon,
      B2bMiniCart,
      B2cMiniCart,
      NavCategoriesLink,
      NavUtilityBarControl,
      NavUtilityBarB2bContainer,
      IdealIcon,
      ApplePayIcon,
      NavBar,
      AccountSelector,
      VendorSelector,
      NotificationsNav,
      NotificationsDropdown,
      NavUtilityBarVendorSelector,
      PdlCarousel,
      PdlCarouselSlide,
    },
    mixins: [trackGlobalEvents],

    data() {
      return {
        isLoaded: false,
      };
    },
    computed: {
      ...mapGetters('advancedOrdering', ['advancedOrderingMode']),
      ...mapState('backend', ['isConsumerFriendlyMode']),

      user() {
        return this.$store.state.user;
      },
    },
    beforeMount() {
      loadLanguageAsync(window.ACC.languageISO).then(() => {
        this.isLoaded = true;
      });
    },

    created() {
      window.addEventListener('resize', this.handleResize);
      this.getCurrentBreakpoint();
    },

    unmounted() {
      window.removeEventListener('resize', this.handleResize);
    },

    methods: {
      ...mapActions('backend', ['toggleConsumerFriendlyMode', 'toggleAdvancedOrderingMode']),
      ...mapMutations('backend', ['setConsumerFriendlyMode', 'setAdvancedOrderingMode']),
      ...mapActions('viewSize', ['handleResize', 'getCurrentBreakpoint']),

      toast(options) {
        return this.$notify(options);
      },

      checkForm(id) {
        const formData = new FormData(document.forms[id]);
        console.table([...formData]);
      },
    },
  });

  const requireComponent = require.context('./components', true, /[A-Z]\w+\.(vue|js)$/);

  requireComponent.keys().forEach((fileName) => {
    const component = requireComponent(fileName);
    const name = upperFirst(
      camelCase(
        first(
          fileName
            .replace(/^\.\/(.*)\.\w+$/, '$1')
            .split('/')
            .reverse()
        )
      )
    );

    app.component(name, component.default || component);
  });

  initInterceptors(PdlToast);
  app.config.globalProperties.$notify = PdlToast;
  app.config.devtools = true;
  app.config.performance = true;

  app.use(store);
  app.use(I18n);
  app.use(VueTippy, {
    directive: 'tippy', // => v-tippy
    component: 'tippy', // => <tippy/>
  });

  app.mount(container);
  return app;
};
const unmountOldApps = function () {
  if (window.reMountedApps?.length) {
    for (let reMountedApp of window.reMountedApps) {
      reMountedApp.unmount();
    }
  }
  window.reMountedApps = [];
};
const reMountVue = function (domElements) {
  domElements.forEach((el) => {
    let app = mountVueAppToElement(el);
    window.reMountedApps.push(app);
  });
};

export {vueConfig, reMountVue, unmountOldApps};
